<template>
  <div class="reports-events cols">
    <div class="filter rows mobile-cols align-items-start">
      <div class="field-group cols align-items-start">
        <label for="vehicle">{{ $t('reports.events.vehicle') }}</label>
        <ChooseVehicles id="vehicles"
                        :preselectAll="false"
                        v-model="selectedVehicles"
                        @update:modelValue="onSelectDeviceId">
        </ChooseVehicles>
      </div>
      <div class="field-group cols align-items-start">
        <label class="typo__label">{{ $t('reports.events.statuses') }}</label>
        <div class="event-statuses">
          <multiselect v-model="selectedStatuses"
                       :options="filteredEventTypes"
                       :multiple="true"
                       :close-on-select="false"
                       :clear-on-select="false"
                       :preserve-search="true"
                       :show-labels="false"
                       :placeholder="$t('settings.generic.select-items')"
                       :show-no-results="false"
                       :show-no-options="false"
                       label="label" track-by="key"
                       class="tracking-multiselect fit-content">
            <template #option="props">
              <div class="tracking-multiselect-with-checkbox">
                <input type="checkbox" :checked="isStatusChecked(props.option.key)" @focus.prevent :key="props.option.key" />
                <span class="option__title">{{ props.option.label }}</span>
              </div>
            </template>
            <template #selection="{ values, isOpen }">
              <span class="multiselect__single"
                    v-if="values.length"
                    v-show="!isOpen">{{ $t('settings.generic.selected-items') }} {{ values.length }}</span>
            </template>
            <template #placeholder>
              <span class="text-danger">
                {{ $t('settings.generic.please-select-item') }}
              </span>
            </template>
            <template #beforeList v-if="this.filteredEventTypes.length > 0">
              <li class="multiselect__element" @click="toggleStatusesSelection">
                <span class="multiselect__option tracking-multiselect-with-checkbox">
                  <input type="checkbox" :checked="areAllStatusesChecked()" @focus.prevent>
                  <span class="option__title"><b>{{ $t('settings.generic.select-all') }}</b></span>
                </span>
              </li>
            </template>
          </multiselect>
        </div>
      </div>
      <div class="field-group cols align-items-start">
        <label for="dateFrom">{{ $t('reports.events.date-from') }}</label>
        <Datepicker
            id="dateFrom"
            :model-value="eventsFilterDateFrom"
            @update:modelValue="onSelectDateFrom"
            :format="$t('settings.generic.date-time-format')"
            :locale="locale"
            :max-date="new Date()"
            auto-apply
        />
      </div>
      <div class="field-group cols align-items-start">
        <label for="dateTo">{{ $t('reports.events.date-to') }}</label>
        <Datepicker
            id="dateTo"
            :model-value="eventsFilterDateTo"
            @update:modelValue="onSelectDateTo"
            :format="$t('settings.generic.date-time-format')"
            :locale="locale"
            :max-date="new Date()"
            auto-apply
        />
      </div>
      <div class="field-group cols align-items-start">
        <label>&nbsp;</label>
        <button type="submit" @click="search" :disabled="!this.isAnyStatusChecked()">
          {{ $t('reports.events.search') }}
        </button>
      </div>
      <div class="field-group cols align-items-start">
        <label>&nbsp;</label>
        <button @click="exportFile" :disabled="!this.isAnyStatusChecked()">
          {{ $t('reports.events.export') }}
        </button>
      </div>
    </div>
    <div class="events-information rows mobile-cols align-items-start">
      <div class="events-information-group cols align-items-start">
        <button class="green-gradient" @click="showAvailableEvents"> {{ $t('reports.events.check-available-events') }} </button>
      </div>
      <div class="events-information-group cols align-items-start">
        <button class="green-gradient" @click="claimInterestInEvents"> {{ $t('reports.events.claim-interest') }} </button>
      </div>
    </div>
    <div class="result-set">
      <div v-if="!hasRecords" class="message info">
        {{ $t('reports.events.result.empty') }}
      </div>
      <div v-else>
        <DataGrid :data-model="dataGridModel"
                  :data="eventsRecords"
                  :interactive="false"
                  :showActionsTableHeader="false"/>
      </div>
    </div>
  </div>
</template>

<script>

import DataGrid from "@/components/pages/partials/DataGrid.vue";
import {mapState} from "vuex";
import {dataManipulator, entityDataManager, GoogleMapsManager} from "@/assets/js/data-manipulation";
import Datepicker from "@vuepic/vue-datepicker";
import {actions, mutations} from "@/assets/js/vue/vuex/actions";
import locale from '@/assets/js/locale';
import notification from "@/assets/js/notification";
import {utils, writeFileXLSX} from "xlsx";
import Multiselect from 'vue-multiselect'
import {EventSeverity, eventsInfo} from "@/assets/js/events-info";
import moment from "moment";
import ChooseVehicles from "@/components/pages/partials/ChooseVehicles.vue";

export default {
  components: {ChooseVehicles, Datepicker, DataGrid, Multiselect},
  name: 'Trips',
  data() {
    return {
      locale: locale.getLocale(),
      selectedVehicles: [],
      selectedStatuses: [],
    };
  },
  computed: {
    ...mapState([
      'eventsFilterDateFrom',
      'eventsFilterDateTo',
      'eventsFilterDeviceIds',
      'eventsRecords',
      'userAssignedEvents',
      'eventsFilterStatuses'
    ]),
    hasRecords() {
      return this.eventsRecords.length > 0;
    },
    dataGridModel() {
      return {
        headers: {
          ['']: 'icon',
          [this.$t('reports.events.result.date')]: 'date',
          [this.$t('reports.events.vehicle')]: 'name',
          [this.$t('reports.events.result.message')]: 'message',
        },
        values: {
          icon: this.getAlarmIcon,
          date: this.getEventDate,
          name: this.getVehicleName,
          message: this.getMessage
        },
        actions: [
          {icon: 'bi bi-geo-alt-fill text-danger font-size-24', buttonClass: 'border-less', handler: this.getEventLocation},
        ]
      };
    },
    filteredEventTypes() {
      if (!this.userAssignedEvents) {
        return [];
      }

      return this.getFilteredEventTypes(this.userAssignedEvents);
    },
    exportData() {
      if (!this.eventsRecords) {
        return [];
      }

      return this.eventsRecords.reduce(
          (result, record) => {
            const row = {
              [this.$t('reports.events.result.date')]: this.getEventDate(record),
              [this.$t('reports.events.vehicle')]: this.getVehicleName(record),
              [this.$t('reports.events.result.message')]: this.getMessage(record),
              [this.$t('reports.events.result.priority')]: this.isWarningSeverity(record) ? this.$t('reports.events.result.high-priority') : '',
            };

            result.push(row);

            return result;
          },
          [],
      );
    },
  },
  methods: {
    getAlarmIcon(event) {
      const result = {class: 'width-15', message: ''};

      if (this.isWarningSeverity(event)) {
        result.icon = 'bi bi-exclamation-circle-fill font-size-24 text-danger px-20';
      }

      return result;
    },
    isWarningSeverity(event) {
      const eventInfo = entityDataManager.getEventInfo(event.type, event.attributes?.alarm);
      return eventInfo.severity >= EventSeverity.warning;
    },
    getEventDate(event) {
      return dataManipulator.iso8601DateStringToLocalMoment(event.eventTime)
          .format(this.$t('settings.generic.date-time-format-moment-extended'));
    },
    getVehicleName(event) {
      return event.vehicleName;
    },
    getMessage(event) {
      return entityDataManager.getTranslatedMessageByEvent(event.type, event.alarmType, event.geofenceName, event.maintenanceName);
    },
    async getEventLocation(event) {

      const result = await this.$store.dispatch(actions.GET_TRACCAR_POSITIONS, {id: event.positionId});
      if (result?.length <= 0) {
        return;
      }

      const position = result[0];
      if (!position) {
        return;
      }

      GoogleMapsManager.openLocationLink(position.latitude, position.longitude);
    },
    onSelectDeviceId(value) {
      this.$store.commit(mutations.SET_EVENTS_FILTER_DEVICE_ID, value);
    },
    onSelectDateFrom(value) {
      this.$store.commit(
          mutations.SET_EVENTS_FILTER_DATE_FROM,
          {date: dataManipulator.dateTimeToString(value)},
      );
    },
    onSelectDateTo(value) {
      this.$store.commit(
          mutations.SET_EVENTS_FILTER_DATE_TO,
          {date: dataManipulator.dateTimeToString(value)},
      );
    },
    onSelectStatuses(value) {
      this.$store.commit(mutations.SET_EVENTS_FILTER_STATUSES, value);
    },
    search() {
      this.$store.dispatch(actions.GET_EVENTS_RECORDS).catch(e => notification.error(this.$t(e.message)));
    },
    exportFile() {
      this.$store.dispatch(actions.GET_EVENTS_RECORDS).
      then(() => {
        const sheetName = this.$t('navbar.reports.events')
        const wb = utils.book_new();
        const ws = utils.json_to_sheet(this.exportData);

        const maxColWidths = this.exportData.reduce(
            (widths, row) => {
              Object.keys(row).forEach(
                  key => {
                    if (widths[key] === undefined) {
                      widths[key] = key.length;
                    }

                    return widths[key] = Math.max(widths[key], row[key].length);
                  },
              );

              return widths;
            },
            {},
        );

        ws['!cols'] = Object.values(maxColWidths).map(colWidth => ({wch: colWidth + 1}));

        utils.book_append_sheet(wb, ws, sheetName);

        writeFileXLSX(wb, sheetName + '.xlsx');
      }).
      catch(e => notification.error(this.$t(e.message)));
    },
    isStatusChecked(key) {
      return this.selectedStatuses.some(x => x.key === key);
    },
    areAllStatusesChecked() {
      return this.selectedStatuses.length === this.filteredEventTypes.length;
    },
    isAnyStatusChecked() {
      return this.selectedStatuses.length > 0;
    },
    toggleStatusesSelection() {
      if (this.filteredEventTypes.length <= 0) {
        return;
      }

      this.onSelectStatuses(this.filteredEventTypes);
      this.selectedStatuses = this.eventsFilterStatuses;
    },
    getFilteredEventTypes(events) {
      const eventTypes = this.getEventTypes();
      const assignableEvents = events.map(x => x.subType ? `${x.type}.${x.subType}` : x.type);
      return eventTypes
          .filter(x => assignableEvents.includes(x.key) && x.allowUseInReports)
          // ensure unique
          .filter((value, index, array) => array.findIndex(x => x.label === value.label) === index);
    },
    getEventTypes(data = eventsInfo, prefix = '') {
      let eventTypes = [];
      for (let key in data) {
        const eventInfo = data[key];
        const eventTypeKey = prefix ? `${prefix}.${key}` : key;
        if (!eventInfo.message) {
          const innerMessages = this.getEventTypes(eventInfo, eventTypeKey);
          eventTypes = [...eventTypes, ...innerMessages];
        }
        else {
          eventTypes.push({
            key: eventTypeKey,
            label: this.$t(eventInfo.message),
            allowUseInReports: eventInfo.allowUseInReports,
            showInAssignableEvents: eventInfo.showInAssignableEvents
          });
        }
      }

      return eventTypes.sort((a, b) => a.label.localeCompare(b.label));
    },
    showAvailableEvents() {
      this.$store.dispatch(actions.GET_ASSIGNABLE_EVENTS)
          .then(events => {

            const filteredEvents = this.getFilteredEventTypes(events)
                .filter(x => x.showInAssignableEvents ?? true);

            let text = '<ol class="assignable-events-container">';
            for (const event of filteredEvents) {
              text += `<li>${ event.label }</li>`;
            }
            text += '</ol>'

            this.$swal.fire({
              html: text,
              width: 'fit-content',
              buttonsStyling: false,
              showCloseButton: true,
              showCancelButton: true,
              confirmButtonText: this.$t('reports.events.claim-interest'),
              cancelButtonText: this.$t('generic.cancel')
            })
                .then((result) => {
                  if (result.isConfirmed) {
                    this.claimInterestInEvents();
                  }
                });
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    async claimInterestInEvents() {
      this.$store.dispatch(actions.CLAIM_INTEREST_IN_EVENTS)
          .then(() => {
            this.$swal.fire({
              html: `${ this.$t('reports.events.claim-interest.thank-you') }</br>${ this.$t('reports.events.claim-interest.will-contact-user') }`,
              buttonsStyling: false,
              showCloseButton: true,
              showCancelButton: true,
              showConfirmButton: false,
              cancelButtonText: this.$t('generic.close'),
              focusCancel: true
            });
          })
          .catch(e => notification.error(this.$t(e.message)));
    }
  },
  mounted() {
    if (!this.eventsFilterDateFrom) {
      this.onSelectDateFrom(moment().startOf('day').toDate());
    }

    if (!this.eventsFilterDateTo) {
      this.onSelectDateTo(moment().endOf('day').toDate());
    }

    this.selectedVehicles = this.eventsFilterDeviceIds;
    this.selectedStatuses = this.eventsFilterStatuses;

    if (this.userAssignedEvents?.length && !this.selectedStatuses?.length) {
      this.toggleStatusesSelection();
    }

    if (this.$route.query.search === '1') {
      this.search();
    }
  },
  watch: {
    selectedStatuses(value) {
      this.onSelectStatuses(value);
    },
    userAssignedEvents() {
      if (!this.selectedStatuses?.length) {
        this.toggleStatusesSelection();
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/variables.scss';
@import "@/assets/scss/button.scss";
  .swal2-confirm {
    @extend button, .green-gradient;
  }

  .assignable-events-container {
    text-align: start;
  }
</style>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.reports-events {
  .filter {
    margin-top: 20px;
    padding: 10px;

    .field-group {
      margin-right: 5px;

      label {
        margin-bottom: 10px;
      }
    }

    .event-statuses {
      width: 230px;
      height: 37px;
    }
  }

  .events-information {
    padding: 0 10px;
    display: flex;
    justify-content: center;

    .events-information-group {
      margin-right: 5px;
    }
  }

  .result-set {
    padding: 10px;

    .table-pad {
      margin-left: 0;
    }
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .reports-events {
    .filter {
      .field-group {
        width: 100%;

        label {
          margin-top: 10px;
        }

        label, input, select, .dp__main, .event-statuses {
          width: 100%;
        }
      }
    }

    .events-information {
      .events-information-group {
        width: 100%;
        margin-top: 5px;
      }
    }

    .result-set {
      .table-pad {
        margin: 0;
        width: 100%;
      }
    }
  }
}
</style>